import { ArrowLink, Button } from "@/components";
import { Attachment } from "@/icons";
import React from "react";

// Centralized type to describe a single CTA/link entry used by SupportBanner
export interface SupportBannerLink {
  text: string;
  href?: string;
  icon?: boolean;
  target?: string;
  isExternal?: boolean;
  handleAction?: () => void;
  variant?: "link" | "button" | "outlineButton" | "outlineButtonHref";
}

/**
 * CTA Components configuration object containing different call-to-action components
 * Each function returns a React component with specific styling and behavior
 */
export const CTA_COMPONENTS = (
  LinkComponent: "a" | React.ComponentType<any>,
) => ({
  /**
   * Renders a link component with optional action handler
   */
  link: ({ text, href, handleAction }: SupportBannerLink) => {
    if (handleAction) {
      return (
        <ArrowLink asButton onClick={handleAction}>
          {text}
        </ArrowLink>
      );
    }

    return (
      <ArrowLink asLink={LinkComponent} href={href ?? ""}>
        {text}
      </ArrowLink>
    );
  },

  /**
   * Renders a basic button component
   */
  button: ({ text, icon, handleAction }: SupportBannerLink) => (
    <Button size="2" className="w-full " onClick={handleAction}>
      {icon && <Attachment className="mr-x8" />}
      <p className="fs-6 fw-bold">{text}</p>
    </Button>
  ),

  /**
   * Renders an outline button with optional icon and action handler
   */
  outlineButton: ({ text, icon, handleAction }: SupportBannerLink) => (
    <Button
      size="2"
      textColor="primary"
      className="w-full bg-inherit"
      border="primary"
      onClick={handleAction}
    >
      {icon && <Attachment className="mr-x8" />}
      <p className="fs-6 fw-bold">{text}</p>
    </Button>
  ),

  /**
   * Renders an outline button that acts as a link
   */
  outlineButtonHref: ({
    text,
    href,
    icon,
    target,
    isExternal,
  }: SupportBannerLink) => (
    <Button
      size="2"
      textColor="primary"
      className="w-full bg-inherit"
      border="primary"
      asLink="a"
      target={target ?? (isExternal ? "_blank" : undefined)}
      href={href ?? ""}
    >
      {icon && <Attachment className="mr-x8" />}
      <p className="fs-6 fw-bold">{text}</p>
    </Button>
  ),
});
